import React, { useEffect, useMemo, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import { getPropertyStatusSx } from "./propertyStatusStyles";

const PLACEHOLDER_IMAGE = "/images/property-placeholder.svg";

export default function PropertyProfileDialog({ property, onClose, onOpenDetails }) {
  const images = useMemo(() => {
    if (!property) return [PLACEHOLDER_IMAGE];
    const gallery = property.images?.length ? property.images : property.image ? [property.image] : [];
    return gallery.length ? gallery : [PLACEHOLDER_IMAGE];
  }, [property]);
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => setImageIndex(0), [property?.id]);

  if (!property) return null;

  const showPreviousImage = () => setImageIndex((current) => (current - 1 + images.length) % images.length);
  const showNextImage = () => setImageIndex((current) => (current + 1) % images.length);

  const address = [
    property.streetAddress,
    [property.city, property.state, property.zipCode].filter(Boolean).join(", "),
    property.country,
  ].filter(Boolean).join(" · ");
  const fields = [
    ["Property type", property.propertyType || "Not specified"],
    ["Current status", property.status || "Occupied"],
    ["Square footage", property.squareFeet === "" || property.squareFeet == null ? "Not specified" : `${Number(property.squareFeet).toLocaleString()} sq. ft.`],
    ["Year built", property.yearBuilt || "Not specified"],
    ["Number of floors", property.numberOfFloors === "" || property.numberOfFloors == null ? "Not specified" : Number(property.numberOfFloors).toLocaleString()],
    ["Lot size", property.lotSizeAcres === "" || property.lotSizeAcres == null ? "Not specified" : `${Number(property.lotSizeAcres).toLocaleString()} acres`],
  ];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 7, color: BRAND_BLUE, fontWeight: 800 }}>
        {property.name || "Unnamed property"}
        <IconButton aria-label="Close property profile" onClick={onClose} sx={{ position: "absolute", top: 10, right: 10 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ position: "relative", bgcolor: "#101827" }}>
          <Box
            component="img"
            src={images[imageIndex]}
            alt={`${property.name || "Property"} photo ${imageIndex + 1} of ${images.length}`}
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = PLACEHOLDER_IMAGE;
            }}
            sx={{ display: "block", width: "100%", height: { xs: 220, sm: 320 }, objectFit: "cover" }}
          />
          {images.length > 1 && (
            <>
              <IconButton aria-label="Previous property image" onClick={showPreviousImage} sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "white", bgcolor: "rgba(0,0,0,.5)", "&:hover": { bgcolor: "rgba(0,0,0,.7)" } }}>
                <ChevronLeftIcon />
              </IconButton>
              <IconButton aria-label="Next property image" onClick={showNextImage} sx={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "white", bgcolor: "rgba(0,0,0,.5)", "&:hover": { bgcolor: "rgba(0,0,0,.7)" } }}>
                <ChevronRightIcon />
              </IconButton>
              <Typography sx={{ position: "absolute", right: 14, bottom: 12, px: 1.25, py: 0.4, borderRadius: 5, color: "white", bgcolor: "rgba(0,0,0,.62)", fontSize: 12, fontWeight: 700 }}>
                {imageIndex + 1} / {images.length}
              </Typography>
              <Box sx={{ position: "absolute", left: "50%", bottom: 14, transform: "translateX(-50%)", display: "flex", gap: 0.75 }}>
                {images.map((image, index) => (
                  <Box component="button" type="button" key={`${image.slice(0, 32)}-${index}`} aria-label={`Show property image ${index + 1}`} onClick={() => setImageIndex(index)} sx={{ width: 9, height: 9, p: 0, border: "1px solid white", borderRadius: "50%", cursor: "pointer", bgcolor: index === imageIndex ? "white" : "rgba(255,255,255,.3)" }} />
                ))}
              </Box>
            </>
          )}
        </Box>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            <Chip label={property.status || "Occupied"} size="small" variant="outlined" sx={{ fontWeight: 800, ...getPropertyStatusSx(property.status) }} />
            {address && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: BRAND_BLUE }}>
                <LocationOnOutlinedIcon sx={{ color: "error.main", fontSize: 19 }} />
                <Typography variant="body2">{address}</Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ mt: 2.5, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" }, gap: 0 }}>
            {fields.map(([label, value]) => (
              <Box key={label} sx={{ py: 1.25, pr: 2, borderBottom: `1px solid ${alpha(BRAND_BLUE, 0.1)}` }}>
                <Typography variant="caption" sx={{ display: "block", color: BRAND_GREEN, fontWeight: 700 }}>{label}</Typography>
                <Typography sx={{ mt: 0.25, color: BRAND_BLUE, fontWeight: 700 }}>{value}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 2.5 }}>
            <Typography variant="caption" sx={{ color: BRAND_GREEN, fontWeight: 700 }}>Building description</Typography>
            <Typography sx={{ mt: 0.35, color: BRAND_BLUE, whiteSpace: "pre-wrap" }}>
              {property.buildingDescription || "No building description has been added."}
            </Typography>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ color: BRAND_GREEN, fontWeight: 700 }}>Available utilities</Typography>
            <Typography sx={{ mt: 0.35, color: BRAND_BLUE, whiteSpace: "pre-wrap" }}>
              {property.utilitiesAvailable || "No utility information has been added."}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
        <Button variant="contained" onClick={() => onOpenDetails(property)} sx={{ bgcolor: BRAND_BLUE, textTransform: "none", fontWeight: 700 }}>
          Open full details
        </Button>
      </DialogActions>
    </Dialog>
  );
}
